import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Lock, ArrowRight, Share2 } from "lucide-react";
import BrainScene from "../components/BrainScene";
import { shareScoreCard } from "../lib/scoreCard";

const API = process.env.REACT_APP_BACKEND_URL;

function visitorId() {
  let v = localStorage.getItem("mscore_vid");
  if (!v) {
    v = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem("mscore_vid", v);
  }
  return v;
}

function ScoreCounter({ value }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf;
    const t0 = performance.now();
    const dur = 1800;
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <span data-testid="mscore-score-value">{n}</span>;
}

const PLACEHOLDERS = [
  'e.g. "A cinematic day-in-the-life reel of a female founder, shot like a Vogue cover story"',
  'e.g. "An app that matches busy women with vetted last-minute glam artists"',
  'e.g. "A raw talking-head video about the burnout nobody posts about"',
  'e.g. "A marketplace app where stylists rent out designer archives by the week"',
];

export default function MScore() {
  const [idea, setIdea] = useState("");
  const [phase, setPhase] = useState("idle"); // idle | analyzing | result
  const [result, setResult] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [email, setEmail] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [limitHit, setLimitHit] = useState(false);
  const [ph] = useState(() => PLACEHOLDERS[Math.floor(Math.random() * PLACEHOLDERS.length)]);
  const resultRef = useRef(null);

  const analyze = async () => {
    if (idea.trim().length < 10) {
      toast.error("Give the engine a little more to work with — a sentence or two.");
      return;
    }
    setPhase("analyzing");
    setResult(null);
    setBreakdown(null);
    try {
      const res = await fetch(`${API}/api/mscore/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idea: idea.trim(), visitor_id: visitorId() }),
      });
      if (res.status === 429) {
        setLimitHit(true);
        setPhase("idle");
        return;
      }
      if (!res.ok) throw new Error("engine");
      const data = await res.json();
      setResult(data);
      setPhase("result");
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    } catch {
      toast.error("The engine misfired. Try again in a moment.");
      setPhase("idle");
    }
  };

  const unlock = async (e) => {
    e.preventDefault();
    setUnlocking(true);
    try {
      const res = await fetch(`${API}/api/mscore/unlock`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ analysis_id: result.analysis_id, email }),
      });
      if (!res.ok) throw new Error("unlock");
      const data = await res.json();
      setBreakdown(data.breakdown);
    } catch {
      toast.error("Couldn't unlock the report. Check your email and try again.");
    } finally {
      setUnlocking(false);
    }
  };

  const share = async () => {
    setSharing(true);
    try {
      const outcome = await shareScoreCard({
        score: result.score,
        verdict: result.verdict,
        regions: result.regions,
        idea: idea.trim(),
      });
      if (outcome === "downloaded") toast.success("Score card saved — post it to your story.");
      if (outcome === "shared") toast.success("Shared. The algorithm thanks you.");
    } catch {
      toast.error("Couldn't create your score card. Try again.");
    } finally {
      setSharing(false);
    }
  };

  const analyzing = phase === "analyzing";

  return (
    <div data-testid="mscore-page" className="bg-jet text-cream min-h-screen">
      {/* HERO */}
      <section className="relative pt-32 pb-8 container-edge">
        <div className="overline text-gold mb-4">Neuro-Editorial Instrument — No. 001</div>
        <h1 className="font-display uppercase text-6xl sm:text-7xl lg:text-8xl leading-[0.9]">
          M Matter<span className="text-gold">.</span>
        </h1>
        <p className="font-serif italic text-xl md:text-2xl text-cream/70 mt-5 max-w-2xl">
          Type your idea — a piece of content or an app — and watch the mind respond. Our engine
          maps where it fires in the audience brain and returns your M Score<span className="align-super text-sm">™</span>.
        </p>
      </section>

      {/* BRAIN + INPUT */}
      <section className="container-edge grid lg:grid-cols-2 gap-10 items-center">
        <div className="relative h-[52vh] lg:h-[68vh] order-1 lg:order-2" data-testid="mscore-brain-canvas">
          <BrainScene activations={result?.regions || []} analyzing={analyzing} />
          <AnimatePresence>
            {analyzing && (
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="absolute bottom-4 left-0 right-0 text-center overline text-gold animate-pulse"
              >
                Reading neural response…
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="order-2 lg:order-1">
          {limitHit ? (
            <div className="border border-cream/20 p-8" data-testid="mscore-limit-message">
              <div className="overline text-gold mb-3">Instrument Cooling Down</div>
              <p className="font-serif italic text-xl text-cream/80">
                You've used all five readings for today. The engine resets at midnight — or skip the
                wait and <Link to="/apply" className="underline text-gold">talk to the humans behind it</Link>.
              </p>
            </div>
          ) : (
            <>
              <label className="overline text-cream/60 block mb-3" htmlFor="mscore-idea">Your content or app idea</label>
              <textarea
                id="mscore-idea"
                data-testid="mscore-idea-input"
                value={idea}
                onChange={(e) => setIdea(e.target.value.slice(0, 600))}
                placeholder={ph}
                rows={4}
                disabled={analyzing}
                className="w-full bg-transparent border border-cream/25 focus:border-gold outline-none p-5 text-cream font-body text-base placeholder:text-cream/30 transition-colors resize-none"
              />
              <div className="flex items-center justify-between mt-4">
                <button
                  data-testid="mscore-analyze-btn"
                  onClick={analyze}
                  disabled={analyzing}
                  className="bg-gold text-jet overline px-8 py-4 hover:bg-cream transition-colors disabled:opacity-40 disabled:cursor-wait"
                >
                  {analyzing ? "Analyzing…" : "Run the Analysis →"}
                </button>
                <div className="overline text-cream/40">
                  {result ? `${result.remaining} of 5 left today` : "5 free readings / day"}
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* RESULT */}
      <div ref={resultRef}>
        <AnimatePresence>
          {result && (
            <motion.section
              initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
              className="container-edge py-20"
              data-testid="mscore-result"
            >
              <div className="grid lg:grid-cols-12 gap-12 border-t border-cream/15 pt-14">
                {/* Score */}
                <div className="lg:col-span-4">
                  <div className="overline text-cream/50 mb-2">Your M Score</div>
                  {result.idea_type && result.idea_type !== "invalid" && (
                    <div data-testid="mscore-idea-type" className="inline-block border border-gold/40 text-gold overline px-3 py-1 mb-4">
                      Read as — {result.idea_type === "app" ? "App Concept" : "Content Concept"}
                    </div>
                  )}
                  <div className="font-display text-[9rem] leading-none text-gold">
                    <ScoreCounter value={result.score} />
                    <span className="text-3xl text-cream/40 ml-2">/100</span>
                  </div>
                  <p className="font-serif italic text-xl text-cream/80 mt-4" data-testid="mscore-verdict">
                    “{result.verdict}”
                  </p>
                  <button
                    data-testid="mscore-share-btn"
                    onClick={share}
                    disabled={sharing}
                    className="mt-6 inline-flex items-center gap-3 border border-gold text-gold overline px-6 py-3 hover:bg-gold hover:text-jet transition-colors disabled:opacity-50"
                  >
                    <Share2 size={14} /> {sharing ? "Creating card…" : "Share My Score"}
                  </button>
                </div>

                {/* Activation map */}
                <div className="lg:col-span-8">
                  <div className="overline text-cream/50 mb-6">Neural Activation Map</div>
                  <div className="space-y-5">
                    {result.regions.map((r, i) => (
                      <motion.div
                        key={r.id}
                        data-testid={`mscore-region-row-${r.id}`}
                        initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 + i * 0.15 }}
                        className="grid grid-cols-12 items-center gap-4"
                      >
                        <div className="col-span-12 sm:col-span-4">
                          <div className="font-display uppercase text-xl">{r.name}</div>
                          <div className="text-sm text-cream/50 font-serif italic">{r.note}</div>
                        </div>
                        <div className="col-span-9 sm:col-span-6 h-[2px] bg-cream/10 relative">
                          <motion.div
                            className="absolute inset-y-0 left-0 bg-gold"
                            initial={{ width: 0 }} animate={{ width: `${r.activation}%` }}
                            transition={{ delay: 0.5 + i * 0.15, duration: 0.9, ease: "easeOut" }}
                          />
                        </div>
                        <div className="col-span-3 sm:col-span-2 font-display text-2xl text-gold text-right">{r.activation}%</div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>

              {/* FULL READ — gated */}
              <div className="mt-20">
                <div className="flex items-baseline gap-4 mb-8">
                  <h2 className="font-display uppercase text-4xl">The Full Read</h2>
                  {!breakdown && <Lock size={16} className="text-gold" />}
                </div>

                {!breakdown ? (
                  <div className="relative border border-cream/15" data-testid="mscore-gate">
                    <div className="p-10 blur-md select-none opacity-50" aria-hidden="true">
                      <p className="font-serif text-lg mb-6">This idea leverages a strong emotional anchor that primes the audience for…</p>
                      <p className="font-serif text-lg mb-6">Strengths include narrative tension, identity signaling, and a clear…</p>
                      <p className="font-serif text-lg">Recommended hook: open on the contradiction, then cut to…</p>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center bg-jet/60 backdrop-blur-[2px]">
                      <form onSubmit={unlock} className="text-center px-6 max-w-md w-full">
                        <div className="overline text-gold mb-3">Unlock the full expert breakdown</div>
                        <p className="font-serif italic text-cream/70 mb-6">
                          Strengths, risks, rewrite recommendations, and two scroll-stopping hooks — free.
                        </p>
                        <input
                          type="email"
                          required
                          data-testid="mscore-email-input"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="your@email.com"
                          className="w-full bg-transparent border border-cream/30 focus:border-gold outline-none px-5 py-4 text-cream text-center placeholder:text-cream/30 mb-4"
                        />
                        <button
                          type="submit"
                          data-testid="mscore-unlock-btn"
                          disabled={unlocking}
                          className="w-full bg-cream text-jet overline py-4 hover:bg-gold transition-colors disabled:opacity-50"
                        >
                          {unlocking ? "Unlocking…" : "Reveal My Full Report"}
                        </button>
                      </form>
                    </div>
                  </div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    className="grid md:grid-cols-2 gap-x-14 gap-y-12 border border-cream/15 p-10"
                    data-testid="mscore-breakdown"
                  >
                    <div className="md:col-span-2">
                      <div className="overline text-gold mb-3">The Read</div>
                      <p className="font-serif text-xl leading-relaxed text-cream/90">{breakdown.summary}</p>
                      {breakdown.emotional_arc && (
                        <p className="font-serif italic text-cream/60 mt-4">Emotional arc — {breakdown.emotional_arc}</p>
                      )}
                    </div>
                    <div>
                      <div className="overline text-gold mb-4">What Works</div>
                      <ul className="space-y-3">
                        {(breakdown.strengths || []).map((s, i) => (
                          <li key={i} className="font-body text-cream/85 flex gap-3"><span className="text-gold">✦</span>{s}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div className="overline text-gold mb-4">Where It Could Fall Flat</div>
                      <ul className="space-y-3">
                        {(breakdown.risks || []).map((s, i) => (
                          <li key={i} className="font-body text-cream/85 flex gap-3"><span className="text-cream/40">—</span>{s}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div className="overline text-gold mb-4">How To Raise The Score</div>
                      <ul className="space-y-3">
                        {(breakdown.recommendations || []).map((s, i) => (
                          <li key={i} className="font-body text-cream/85 flex gap-3"><span className="text-gold">{i + 1}.</span>{s}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div className="overline text-gold mb-4">Hooks Worth Stealing</div>
                      <ul className="space-y-4">
                        {(breakdown.hooks || []).map((s, i) => (
                          <li key={i} className="font-serif italic text-lg text-cream/90">“{s}”</li>
                        ))}
                      </ul>
                      {breakdown.platforms?.length > 0 && (
                        <div className="mt-6 overline text-cream/50">Best on — {breakdown.platforms.join(" · ")}</div>
                      )}
                    </div>
                  </motion.div>
                )}
              </div>

              {/* CTAs */}
              <div className="grid md:grid-cols-2 gap-6 mt-20">
                <Link
                  to="/apply"
                  data-testid="mscore-cta-apply"
                  className="group border border-cream/20 p-10 hover:border-gold transition-colors"
                >
                  <div className="overline text-cream/50 mb-2">Want a team that scores 90s for you?</div>
                  <div className="font-display uppercase text-4xl flex items-center gap-3 group-hover:text-gold transition-colors">
                    Apply to Work With Us <ArrowRight className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </Link>
                <Link
                  to="/studio"
                  data-testid="mscore-cta-studio"
                  className="group border border-cream/20 p-10 hover:border-gold transition-colors"
                >
                  <div className="overline text-cream/50 mb-2">Learn the psychology yourself</div>
                  <div className="font-display uppercase text-4xl flex items-center gap-3 group-hover:text-gold transition-colors">
                    Explore The Studio <ArrowRight className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </Link>
              </div>
            </motion.section>
          )}
        </AnimatePresence>
      </div>

      {/* footnote */}
      <div className="container-edge pb-16 pt-8">
        <p className="overline text-cream/25 max-w-xl">
          M Matter is an editorial instrument powered by AI and behavioral-marketing heuristics.
          The M Score is delightfully opinionated, not a medical device.
        </p>
      </div>
    </div>
  );
}
